import { useState } from "react";
import { toast } from "sonner";
import { Landmark, FileDown, Receipt } from "lucide-react";
import { useLang } from "../../lib/i18n";
import { useFetch } from "../../lib/useFetch";
import { api, download, errMsg, eur, fmtDate } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { FileUpload, StatusBadge } from "../../components/site/FileUpload";
import { PayPalPay } from "../../components/site/PayPalPay";

const OrderReceiptForm = ({ o, onDone }) => {
  const { t } = useLang();
  const [f, setF] = useState({ amount: o.balance, path: "", filename: "" });
  const submit = async () => {
    if (!f.path || !(Number(f.amount) > 0)) return toast.error(`${t("amount")} + ${t("upload_receipt")}`);
    try {
      await api.post(`/me/orders/${o.id}/receipt`, { ...f, amount: Number(f.amount) });
      toast.success(t("receipt_sent"));
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="mt-4 flex w-full flex-wrap items-end gap-3 rounded-2xl bg-slate-50 p-4" data-testid={`order-receipt-form-${o.id}`}>
      <label className="text-xs font-semibold text-slate-600">{t("amount")} (€)
        <Input type="number" max={o.balance} value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className="mt-1 h-10 w-32 rounded-xl" data-testid={`order-receipt-amount-${o.id}`} /></label>
      <FileUpload label={t("upload_receipt")} value={f.filename} onUploaded={(x) => setF({ ...f, path: x.path, filename: x.filename })} testid={`order-receipt-file-${o.id}`} />
      <button onClick={submit} className="btn-buoy !py-2" data-testid={`order-receipt-submit-${o.id}`}>{t("send")}</button>
    </div>
  );
};

const OrderCard = ({ o, reload }) => {
  const { t, lang, ts } = useLang();
  const [form, setForm] = useState(false);
  const pending = o.receipts.some((r) => r.status === "pending");
  return (
    <div className="rounded-3xl border-2 border-dashed border-[#00A8B5] bg-white p-5" data-testid={`my-order-${o.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-start gap-3"><Landmark className="mt-0.5 shrink-0 text-[#00A8B5]" />
          <div><p className="font-semibold text-[#0A2540]">{t("order_number")} {o.number} · {fmtDate(o.created_at, lang)}</p>
            <p className="text-sm text-slate-500">{t("order_single_payment")} · {o.registration_ids.length} {t(o.registration_ids.length === 1 ? "order_item" : "order_items")}{o.discount_code ? ` · ${t("cart_discount")} ${o.discount_code}` : ""}</p></div></div>
        <div className="text-right"><p className="font-display text-2xl font-extrabold text-[#0A2540]">{eur(o.total)}</p>
          <p className="text-xs text-slate-500">{t("paid")} {eur(o.paid)} · {t("balance")} <b data-testid={`my-order-balance-${o.id}`}>{eur(o.balance)}</b></p></div>
      </div>
      {o.companies?.length > 0 && (
        <div className="mt-3 space-y-2" data-testid={`my-order-companies-${o.id}`}>
          {o.companies.map((c) => (
            <div key={c.id} className="rounded-2xl bg-slate-50 p-3 text-sm text-slate-600" data-testid={`my-order-company-${o.id}-${c.id}`}>
              <p className="font-semibold text-[#0A2540]">{t("organizer")}: {c.name}{c.vat_number ? ` · VAT ${c.vat_number}` : ""}</p>
              <p>{[c.address, [c.zip, c.city].filter(Boolean).join(" "), c.country].filter(Boolean).join(", ")}{c.email ? ` · ${c.email}` : ""}</p>
              {c.iban && <p className="font-mono text-xs">IBAN <b>{c.iban}</b>{c.swift ? ` · SWIFT/BIC ${c.swift}` : ""} · {t("order_reference")}: <b>{o.number}</b></p>}
            </div>
          ))}
        </div>
      )}
      {o.receipts.length > 0 && (
        <div className="mt-3 space-y-1 text-sm" data-testid={`my-order-receipts-${o.id}`}>
          {o.receipts.map((r) => <p key={r.id} className="flex items-center justify-between gap-2 text-slate-600"><span className="flex items-center gap-1"><Receipt size={13} />{eur(r.amount)} · {fmtDate(r.created_at, lang)}</span><StatusBadge status={r.status} label={ts(r.status)} /></p>)}
        </div>
      )}
      <div className="mt-4 flex flex-wrap gap-2">
        <button onClick={() => download(`/me/orders/${o.id}/pdf`, `${o.number}.pdf`).catch(() => toast.error("PDF"))} className="inline-flex items-center gap-2 rounded-full border-2 border-[#0A2540] px-4 py-2 text-sm font-semibold text-[#0A2540]" data-testid={`my-order-pdf-${o.id}`}><FileDown size={15} />{t("order_pdf")}</button>
        {o.balance > 0 && !pending && <button onClick={() => setForm(!form)} className="inline-flex items-center gap-2 rounded-full bg-[#0A2540] px-4 py-2 text-sm font-semibold text-white" data-testid={`my-order-upload-${o.id}`}><Receipt size={15} />{t("order_upload_receipt")}</button>}
        {pending && <p className="self-center text-xs text-slate-500" data-testid={`my-order-receipt-pending-${o.id}`}>{t("order_receipt_pending")}</p>}
      </div>
      {form && <OrderReceiptForm o={o} onDone={() => { setForm(false); reload(); }} />}
      {!pending && <PayPalPay order={o} onPaid={reload} />}
    </div>
  );
};

export const MyOrders = () => {
  const { data, reload } = useFetch("/me/orders");
  if (!data?.length) return null;
  return <div className="mb-8 space-y-3" data-testid="my-orders">{data.map((o) => <OrderCard key={o.id} o={o} reload={reload} />)}</div>;
};
